import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { fetchAdConfigurations, fetchAdLogCodeLists } from '../api/restapi';

// ─── Select styles — identical to StartRemoteDebug.js ────────────────────────
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '33px',
    borderColor: state.isFocused ? '#1a56db' : '#d1d5db',
    borderRadius: '6px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26,86,219,.08)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#1a56db' : '#9ca3af' },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
  }),
  indicatorsContainer: (base) => ({
    ...base,
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: '13px',
    color: '#9ca3af',
    margin: 0,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: '13px',
    color: '#1f2937',
    margin: 0,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: '13px',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '13px',
    color: state.isSelected ? 'white' : '#1f2937',
    padding: '8px 12px',
    backgroundColor: state.isSelected
      ? '#1a56db'
      : state.isFocused
      ? '#f0f4ff'
      : 'white',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

// ─── Sequence helpers (module-level counters, reset on component mount via keys) ─
let adEntrySeq = 0;
let ruleSeq = 0;
const newAdId = () => ++adEntrySeq;
const newRuleId = () => ++ruleSeq;

const makeAdEntry = () => ({
  id: newAdId(),
  adConfig: null,
  rules: [],
  schedApplyTo: [],
  schedStart: '',
  schedDuration: '',
  schedWindow: '',
  activeDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
});

const makeRule = (triggerType) => ({
  id: newRuleId(),
  triggerType,          // 'PRE' | 'MAIN'
  contextCollectionRule: null,  // { value: id, label, ruleData }
  logCodeList: null,            // { value: logCodesIndex, label, id }
  ruleDetailOpen: false,
});

// ─── TelemetryCollectorConfig ─────────────────────────────────────────────────
/**
 * Telemetry Collector Config section for the Start Remote Diagnostics screen.
 *
 * Props:
 *   onPayloadChange(adPayload) — called whenever the AD payload changes.
 *     adPayload is either null (no AD) or:
 *     {
 *       adCollectorId: number,          // collector id from diagnostic-data, or null if not yet resolved
 *       adCollectorConfigs: [{ adConfigurationId, rules: [{triggerType, contextCollectionRuleId, logCodesIndex}] }]
 *     }
 *   contextCollectionRuleOptions — array of { value, label, ruleType, ruleData }
 *     (fetched by the parent; reused from the RD section).
 *   fieldErrors — object of field-key → bool for validation highlighting.
 *   diagnosticDataOptions — array of { value, label } from /api/v1/diagnostic-data
 *     (fetched by the parent); used to resolve the AD WWAN collector id.
 */
function TelemetryCollectorConfig({
  onPayloadChange,
  contextCollectionRuleOptions = [],
  fieldErrors = {},
  diagnosticDataOptions = [],
  onBrowseRules,
  onBrowseLogCodes,
  collectorType: collectorTypeProp,
  onCollectorTypeChange,
  adCommand: adCommandProp,
  onAdCommandChange,
  adEntries: adEntriesProp,
  onAdEntriesChange,
}) {
  // Controlled from parent when props provided, otherwise local
  const [collectorTypeLocal, setCollectorTypeLocal] = useState('');
  const [adCommandLocal, setAdCommandLocal] = useState('');
  const [adEntriesLocal, setAdEntriesLocal] = useState([]);
  const collectorType = collectorTypeProp !== undefined ? collectorTypeProp : collectorTypeLocal;
  const adCommand = adCommandProp !== undefined ? adCommandProp : adCommandLocal;
  const adEntries = adEntriesProp !== undefined ? adEntriesProp : adEntriesLocal;
  const setCollectorType = (v) => { setCollectorTypeLocal(v); onCollectorTypeChange?.(v); };
  const setAdCommand = (v) => { setAdCommandLocal(v); onAdCommandChange?.(v); };
  const setAdEntries = (v) => {
    const next = typeof v === 'function' ? v(adEntries) : v;
    setAdEntriesLocal(next);
    onAdEntriesChange?.(next);
  };
  const [adConfigOptions, setAdConfigOptions] = useState([]);
  const [logCodeListOptions, setLogCodeListOptions] = useState([]);
  const [modalAd, setModalAd] = useState(null);
  const [openTrigPicker, setOpenTrigPicker] = useState(null);

  // ── Fetch AD configurations ──────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchAdConfigurations();
        if (Array.isArray(data)) {
          setAdConfigOptions(
            data.map((cfg) => ({
              value: cfg.id,
              label: cfg.label,
              adData: cfg,
            }))
          );
        }
      } catch (_) {}
    };
    load();
  }, []);

  // ── Fetch AD log code lists ──────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchAdLogCodeLists();
        if (Array.isArray(data)) {
          const opts = data.map((lcl) => ({
            value: lcl.logCodesIndex,
            label: lcl.name,
            id: lcl.id,
            isDefault: lcl.isDefault,
          }));
          setLogCodeListOptions(opts);
        }
      } catch (_) {}
    };
    load();
  }, []);

  // ── Resolve AD WWAN collector id from diagnosticDataOptions ─────────────
  const adCollectorEntry = diagnosticDataOptions.find(
    (opt) => opt.label === 'AD'
  ) || null;

  // ── Notify parent of payload changes ────────────────────────────────────
  const buildAndNotify = useCallback(
    (type, cmd, entries) => {
      if (type !== 'adwwan') {
        onPayloadChange(null);
        return;
      }
      const adCollectorId = adCollectorEntry ? adCollectorEntry.value : null;
      const payload = { adCollectorId, adCommand: cmd };
      if (cmd === 'CONFIGURE_ENABLE') {
        payload.adCollectorConfigs = entries
          .filter((e) => e.adConfig)
          .map((e) => ({
            adConfigurationId: e.adConfig.value,
            rules: e.rules
              .filter((r) => r.contextCollectionRule && r.logCodeList)
              .map((r) => ({
                triggerType: r.triggerType,
                contextCollectionRuleId: r.contextCollectionRule.value,
                logCodesIndex: r.logCodeList.value,
              })),
          }));
      }
      onPayloadChange(payload);
    },
    [onPayloadChange, adCollectorEntry]
  );

  // Re-notify on diagnosticDataOptions change (collector id may have resolved)
  useEffect(() => {
    buildAndNotify(collectorType, adCommand, adEntries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnosticDataOptions]);

  // ── Collector type handler ───────────────────────────────────────────────
  const onCollectorChange = (val) => {
    setCollectorType(val);
    setAdCommand('');
    setAdEntries([]);
    buildAndNotify(val, '', []);
  };

  // ── Command handler ──────────────────────────────────────────────────────
  const onCommandChange = (val) => {
    setAdCommand(val);
    const nextEntries = val === 'CONFIGURE_ENABLE' && adEntries.length === 0
      ? [makeAdEntry()]
      : adEntries;
    if (val === 'CONFIGURE_ENABLE' && adEntries.length === 0) setAdEntries(nextEntries);
    buildAndNotify(collectorType, val, val === 'CONFIGURE_ENABLE' && adEntries.length === 0 ? nextEntries : adEntries);
  };

  // ── AD entry handlers ────────────────────────────────────────────────────
  const addAdEntry = () => {
    const updated = [...adEntries, makeAdEntry()];
    setAdEntries(updated);
    buildAndNotify(collectorType, adCommand, updated);
  };

  const removeAdEntry = (id) => {
    const updated = adEntries.filter((e) => e.id !== id);
    setAdEntries(updated);
    buildAndNotify(collectorType, adCommand, updated);
  };

  const onAdConfigSelect = (entId, opt) => {
    const updated = adEntries.map((e) =>
      e.id === entId ? { ...e, adConfig: opt } : e
    );
    setAdEntries(updated);
    buildAndNotify(collectorType, adCommand, updated);
  };

  // ── Rule handlers ────────────────────────────────────────────────────────
  const addRule = (entId, triggerType) => {
    const defaultLogCodeList =
      logCodeListOptions.find((o) => o.isDefault) || null;
    const newRule = { ...makeRule(triggerType), logCodeList: defaultLogCodeList };
    const updated = adEntries.map((e) =>
      e.id === entId ? { ...e, rules: [...e.rules, newRule] } : e
    );
    setAdEntries(updated);
    setOpenTrigPicker(null);
    buildAndNotify(collectorType, adCommand, updated);
  };

  const removeRule = (entId, ruleId) => {
    const updated = adEntries.map((e) =>
      e.id === entId
        ? { ...e, rules: e.rules.filter((r) => r.id !== ruleId) }
        : e
    );
    setAdEntries(updated);
    buildAndNotify(collectorType, adCommand, updated);
  };

  const onRuleCCR = (entId, ruleId, opt) => {
    const updated = adEntries.map((e) =>
      e.id !== entId
        ? e
        : {
            ...e,
            rules: e.rules.map((r) =>
              r.id === ruleId
                ? { ...r, contextCollectionRule: opt, ruleDetailOpen: !!opt }
                : r
            ),
          }
    );
    setAdEntries(updated);
    buildAndNotify(collectorType, adCommand, updated);
  };

  const onRuleLogCodeList = (entId, ruleId, opt) => {
    const updated = adEntries.map((e) =>
      e.id !== entId
        ? e
        : {
            ...e,
            rules: e.rules.map((r) =>
              r.id === ruleId ? { ...r, logCodeList: opt } : r
            ),
          }
    );
    setAdEntries(updated);
    buildAndNotify(collectorType, adCommand, updated);
  };

  const toggleRuleDetail = (entId, ruleId) => {
    setAdEntries((ents) =>
      ents.map((e) =>
        e.id !== entId
          ? e
          : {
              ...e,
              rules: e.rules.map((r) =>
                r.id === ruleId ? { ...r, ruleDetailOpen: !r.ruleDetailOpen } : r
              ),
            }
      )
    );
  };

  const toggleTrigPicker = (entId) =>
    setOpenTrigPicker((cur) => (cur === entId ? null : entId));

  // ── Schedule handlers ────────────────────────────────────────────────────
  const onSchedField = (entId, field, val) => {
    const updated = adEntries.map((e) => e.id === entId ? { ...e, [field]: val } : e);
    setAdEntries(updated);
    buildAndNotify(collectorType, adCommand, updated);
  };

  const onSchedApply = (entId, val) => {
    const updated = adEntries.map((e) => e.id === entId ? { ...e, schedApplyTo: val } : e);
    setAdEntries(updated);
    buildAndNotify(collectorType, adCommand, updated);
  };

  const toggleActiveDay = (entId, dayKey, checked) => {
    const updated = adEntries.map((e) => {
      if (e.id !== entId) return e;
      return { ...e, activeDays: { ...e.activeDays, [dayKey]: checked } };
    });
    setAdEntries(updated);
    buildAndNotify(collectorType, adCommand, updated);
  };

  // Close trigger picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.tel-trig-pick-wrap')) setOpenTrigPicker(null);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // ── Modal helpers ────────────────────────────────────────────────────────
  const openConfigModal = (adData) => setModalAd(adData);
  const closeConfigModal = () => setModalAd(null);

  // ── Validation helpers ───────────────────────────────────────────────────
  /**
   * Returns an array of human-readable error strings for the AD section.
   * Parent calls this via validateAd() — we expose it via a ref-compatible
   * prop pattern: the parent simply reads the returned state from onPayloadChange.
   */

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  const renderPropBar = (adData) => {
    if (!adData) return null;
    const pre = adData.handles?.filter((h) => h.triggerGroup === 'PRE') || [];
    const main = adData.handles?.filter((h) => h.triggerGroup === 'MAIN') || [];
    const stop = adData.handles?.filter((h) => h.triggerGroup === 'STOP') || [];
    const isEnabled =
      adData.status === 'ENABLED' || adData.status === 'Enabled';
    return (
      <div className="ad-prop-bar">
        <div className="prop-pills">
          <div className="prop-pill">
            <span className="prop-key">Technology</span>
            <span className="prop-val">{adData.technology || '—'}</span>
          </div>
          <div className="prop-pill">
            <span className="prop-key">Platform</span>
            <span className="prop-val">{adData.platform || '—'}</span>
          </div>
          <div className="prop-pill">
            <span className="prop-key">Status</span>
            <span className="prop-val">
              <span
                style={{
                  color: isEnabled ? 'var(--green, #16a34a)' : 'var(--gray-400, #9ca3af)',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: isEnabled
                      ? 'var(--green, #16a34a)'
                      : 'var(--gray-400, #9ca3af)',
                    display: 'inline-block',
                  }}
                />
                {adData.status}
              </span>
            </span>
          </div>
          <div className="prop-pill">
            <span className="prop-key">Triggers</span>
            <span className="prop-val trig-wrap">
              {pre.length > 0 && (
                <span key="pre" className="prop-trig pre">
                  {pre.length} Pre
                </span>
              )}
              {main.length > 0 && (
                <span key="main" className="prop-trig main">
                  {main.length} Main
                </span>
              )}
              {stop.length > 0 && (
                <span key="stop" className="prop-trig stop">
                  {stop.length} Stop
                </span>
              )}
              {pre.length === 0 && main.length === 0 && stop.length === 0 && (
                <span style={{ color: 'var(--gray-400, #9ca3af)' }}>—</span>
              )}
            </span>
          </div>
        </div>
        <button
          className="view-config-btn"
          onClick={(e) => {
            e.stopPropagation();
            openConfigModal(adData);
          }}
        >
          <svg
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          View Full Config
        </button>
      </div>
    );
  };

  const renderRuleDetails = (ruleData) => {
    if (!ruleData) return null;
    return (
      <div className="rule-details-grid">
        <div className="rule-detail-card info-card">
          <div className="rdc-header">Rule Info</div>
          <div className="rdc-row">
            <span className="rdc-key">Rule Type</span>
            <span className="rdc-val">{ruleData.ruleType || '—'}</span>
          </div>
          <div className="rdc-row">
            <span className="rdc-key">Label</span>
            <span className="rdc-val">{ruleData.label || ruleData.name || '—'}</span>
          </div>
        </div>
        {ruleData.hysteresis && (
          <div className="rule-detail-card hyst-card">
            <div className="rdc-header">Hysteresis</div>
            <div className="rdc-row">
              <span className="rdc-key">Off Hysteresis (ms)</span>
              <span className="rdc-val">{ruleData.hysteresis.off ?? '—'}</span>
            </div>
            <div className="rdc-row">
              <span className="rdc-key">On Hysteresis (ms)</span>
              <span className="rdc-val">{ruleData.hysteresis.on ?? '—'}</span>
            </div>
          </div>
        )}
        {ruleData.trigger && (
          <div className="rule-detail-card trig-card">
            <div className="rdc-header">Trigger</div>
            <div className="rdc-row">
              <span className="rdc-key">Start Trigger Rule ID</span>
              <span className="rdc-val">{ruleData.trigger.startId ?? '—'}</span>
            </div>
            <div className="rdc-row">
              <span className="rdc-key">Stop Trigger Rule ID</span>
              <span className="rdc-val">{ruleData.trigger.stopId ?? '—'}</span>
            </div>
            <div className="rdc-row">
              <span className="rdc-key">Stop Trigger Duration (ms)</span>
              <span className="rdc-val">{ruleData.trigger.stopDuration ?? '—'}</span>
            </div>
          </div>
        )}
        {ruleData.quota && (
          <div className="rule-detail-card quota-card">
            <div className="rdc-header">Quota</div>
            <div className="rdc-row">
              <span className="rdc-key">Max Size (bytes)</span>
              <span className="rdc-val">{ruleData.quota.maxSize ?? '—'}</span>
            </div>
            <div className="rdc-row">
              <span className="rdc-key">Max Occurrence Count</span>
              <span className="rdc-val">{ruleData.quota.maxOccurrence ?? '—'}</span>
            </div>
            <div className="rdc-row">
              <span className="rdc-key">Time Window (s)</span>
              <span className="rdc-val">{ruleData.quota.timeWindow ?? '—'}</span>
            </div>
            <div className="rdc-row">
              <span className="rdc-key">Max Time Window (ms)</span>
              <span className="rdc-val">{ruleData.quota.maxTimeWindow ?? '—'}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRuleRow = (entry, rule, rIdx) => {
    const chipClass = rule.triggerType === 'PRE' ? 'chip-pre' : 'chip-main';
    const chipLabel = rule.triggerType === 'PRE' ? 'Pre-Trigger' : 'Main-Trigger';
    const maskErrKey = `tel_mask_${entry.id}_${rule.id}`;

    const usedCCRsInGroup = entry.rules
      .filter(r => r.id !== rule.id && r.triggerType === rule.triggerType && r.contextCollectionRule)
      .map(r => r.contextCollectionRule.value);

    return (
      <div className="rule-row" key={rule.id}>
        <div className="rule-row-header">
          <span className={`trig-type-chip ${chipClass}`}>{chipLabel}</span>
          <span className="rule-row-num">Rule {rIdx + 1}</span>
          <button
            className="rule-row-remove"
            onClick={() => removeRule(entry.id, rule.id)}
          >
            ✕ Remove
          </button>
        </div>
        <div className="rule-row-grid">
          <div className="field">
            <label>
              Context Collection Rule
              {onBrowseRules && (
                <span className="ccr-browse" onClick={onBrowseRules}>
                  Browse all rules ↗
                </span>
              )}
            </label>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={contextCollectionRuleOptions}
              value={rule.contextCollectionRule}
              onChange={(opt) => onRuleCCR(entry.id, rule.id, opt)}
              placeholder="Select rule..."
              isClearable
              isOptionDisabled={(opt) => usedCCRsInGroup.includes(opt.value)}
              formatOptionLabel={(option, { context }) => {
                if (context === 'value') return <span>{option.label}</span>;
                return (
                  <div>
                    <div>{option.label}</div>
                    {option.ruleType && (
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {option.ruleType}
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </div>
          <div className="field">
            <label>
              Log Code <span className="req">*</span>
              {onBrowseLogCodes && (
                <span className="ccr-browse" onClick={onBrowseLogCodes}>
                  Browse all log codes ↗
                </span>
              )}
            </label>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              className={fieldErrors[maskErrKey] ? 'select-error' : ''}
              options={logCodeListOptions}
              value={rule.logCodeList}
              onChange={(opt) => onRuleLogCodeList(entry.id, rule.id, opt)}
              placeholder="Select log code..."
              isClearable
            />
            {fieldErrors[maskErrKey] && (
              <span className="err-msg show">Please select a log code.</span>
            )}
          </div>
        </div>
        {rule.contextCollectionRule?.ruleData && (
          <div>
            <button
              className={`rule-detail-toggle${rule.ruleDetailOpen ? ' open' : ''}`}
              onClick={() => toggleRuleDetail(entry.id, rule.id)}
            >
              <svg
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
              {rule.ruleDetailOpen ? '▲ Hide Rule Details' : '▼ Show Rule Details'}
            </button>
            {rule.ruleDetailOpen &&
              renderRuleDetails(rule.contextCollectionRule.ruleData)}
          </div>
        )}
      </div>
    );
  };

  const renderADBlock = (entry, idx) => {
    const adSelErrKey = `tel_adSel_${entry.id}`;
    return (
      <div className="ad-block" key={entry.id}>
        <div className="ad-block-header">
          <div className="ad-block-title">
            <div className="ad-idx-badge">{idx + 1}</div>
            &nbsp;AD Configuration {idx + 1}
          </div>
          {adEntries.length > 1 && (
            <button
              className="ad-remove-btn"
              onClick={() => removeAdEntry(entry.id)}
            >
              <svg
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>{' '}
              Remove
            </button>
          )}
        </div>

        {/* AD config selector */}
        <div style={{ marginBottom: 10 }}>
          <div className="field">
            <label>
              Select AD Configuration <span className="req">*</span>
            </label>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              className={fieldErrors[adSelErrKey] ? 'select-error' : ''}
              options={adConfigOptions}
              value={entry.adConfig}
              onChange={(opt) => onAdConfigSelect(entry.id, opt)}
              placeholder="Choose an AD configuration..."
              isClearable
            />
            {fieldErrors[adSelErrKey] && (
              <span className="err-msg show">
                Please select an AD configuration.
              </span>
            )}
          </div>
        </div>

        {/* Properties bar */}
        {entry.adConfig && renderPropBar(entry.adConfig.adData)}

        {/* Rules area */}
        <div style={{ marginTop: 14 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <label
              style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-600, #4b5563)' }}
            >
              Context Collection Rules
            </label>
            <div className="tel-trig-pick-wrap trig-pick-wrap">
              <button
                className="ctx-rule-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTrigPicker(entry.id);
                }}
              >
                + Add Rule{' '}
                <svg
                  width="10"
                  height="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                  style={{ marginLeft: 3, verticalAlign: 'middle' }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              <div
                className={`trig-pick-popup${
                  openTrigPicker === entry.id ? ' open' : ''
                }`}
              >
                <div className="trig-pick-label">Select Trigger Type</div>
                <div
                  className="trig-pick-item"
                  onClick={() => addRule(entry.id, 'PRE')}
                >
                  <span className="trig-pick-dot dot-pre" /> Pre-Trigger
                </div>
                <div
                  className="trig-pick-item"
                  onClick={() => addRule(entry.id, 'MAIN')}
                >
                  <span className="trig-pick-dot dot-main" /> Main-Trigger
                </div>
              </div>
            </div>
          </div>
          <div>
            {entry.rules.length > 0 ? (
              entry.rules.map((rule, rIdx) => renderRuleRow(entry, rule, rIdx))
            ) : (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--gray-400, #9ca3af)',
                  padding: '10px 0',
                }}
              >
                No rules added yet. Click "+ Add Rule" to add a Pre-Trigger or
                Main-Trigger rule.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderHandleCards = (handles, groupLabel, colorClass) => {
    if (!handles || handles.length === 0) return null;
    return handles.map((h, i) => (
      <div
        className={`modal-handle-card ${colorClass}`}
        key={`${h.handleMasterId || h.handleId || i}-${groupLabel}`}
      >
        <div className="mhc-header">
          <span className="mhc-badge">{groupLabel}</span>
          <span className="mhc-name">{h.triggerName || h.name || '—'}</span>
          <span className="mhc-id">{h.handleMasterId || h.handleId || ''}</span>
        </div>
        <div className="mhc-grid">
          <div>
            <span className="mhc-key">Metric</span>
            <span className="mhc-val">{h.metric || '—'}</span>
          </div>
          <div>
            <span className="mhc-key">Class</span>
            <span className="mhc-val">{h.triggerClass || h.trigClass || '—'}</span>
          </div>
        </div>
      </div>
    ));
  };

  const renderConfigModal = () => {
    const ad = modalAd;
    const preHandles = ad?.handles?.filter((h) => h.triggerGroup === 'PRE') || [];
    const mainHandles = ad?.handles?.filter((h) => h.triggerGroup === 'MAIN') || [];
    const stopHandles = ad?.handles?.filter((h) => h.triggerGroup === 'STOP') || [];
    const noHandles =
      preHandles.length === 0 &&
      mainHandles.length === 0 &&
      stopHandles.length === 0;
    return (
      <div
        className={`cfg-modal-overlay${ad ? ' open' : ''}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) closeConfigModal();
        }}
      >
        <div className="cfg-modal">
          <div className="cfg-modal-header">
            <h3>{ad ? ad.label : 'AD Configuration'}</h3>
            <button className="cfg-modal-close" onClick={closeConfigModal} title="Close">
              ✕
            </button>
          </div>
          <div className="cfg-modal-body">
            {ad && (
              <>
                <div className="modal-summary-row">
                  <div className="modal-meta-pill">
                    <span className="mm-key">AD ID</span>
                    <span className="mm-val">{ad.adId || '—'}</span>
                  </div>
                  <div className="modal-meta-pill">
                    <span className="mm-key">Technology</span>
                    <span className="mm-val">{ad.technology || '—'}</span>
                  </div>
                  <div className="modal-meta-pill">
                    <span className="mm-key">Platform</span>
                    <span className="mm-val">{ad.platform || '—'}</span>
                  </div>
                  <div className="modal-meta-pill">
                    <span className="mm-key">Status</span>
                    <span className="mm-val">
                      <span
                        className={`modal-status ${
                          ad.status === 'ENABLED' || ad.status === 'Enabled'
                            ? 'enabled'
                            : 'disabled'
                        }`}
                      >
                        ● {ad.status}
                      </span>
                    </span>
                  </div>
                </div>
                {ad.description && (
                  <p className="modal-desc">{ad.description}</p>
                )}
                <hr className="modal-divider" />
                <div className="modal-handles-section">
                  {renderHandleCards(preHandles, 'PRE', 'pre-card')}
                  {renderHandleCards(mainHandles, 'MAIN', 'main-card')}
                  {renderHandleCards(stopHandles, 'STOP', 'stop-card')}
                  {noHandles && (
                    <p
                      style={{
                        color: 'var(--gray-400, #9ca3af)',
                        fontSize: 12.5,
                        padding: '8px 0',
                      }}
                    >
                      No trigger handles configured.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="config-panel tel-panel">
      <div className="config-panel-header">
        <div className="tel-panel-icon">
          <svg viewBox="0 0 24 24" strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
        </div>
        <div>
          <div className="tel-panel-title">Telemetry Collector Config</div>
          <div style={{ fontSize: 11, color: '#0d9488', marginTop: 1 }}>
            Anomaly Detection &amp; real-time data collection &nbsp;·&nbsp;
            <span style={{ color: '#f59e0b', fontWeight: 600 }}>For testing purposes only</span>
          </div>
        </div>
        <span className="tel-panel-badge">Telemetry Config</span>
      </div>

      <div className="tel-panel-body">
        {/* Collector type + Command row — same diag-row grid as RD section */}
        <div className="diag-row">
          <div className="field">
            <label>Collector Type <span className="req">*</span></label>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              className={fieldErrors['tel_collectorType'] ? 'select-error' : ''}
              options={[
                { value: 'mbb', label: 'MBB Collector' },
                { value: 'adwwan', label: 'AD WWAN Collector' },
              ]}
              value={
                collectorType
                  ? { value: collectorType, label: collectorType === 'adwwan' ? 'AD WWAN Collector' : 'MBB Collector' }
                  : null
              }
              onChange={(opt) => onCollectorChange(opt ? opt.value : '')}
              placeholder="Select..."
              isClearable
            />
            {fieldErrors['tel_collectorType'] && (
              <span className="err-msg show">Please select a collector type.</span>
            )}
          </div>

          <div className="field">
            <label>Command {collectorType === 'adwwan' && <span className="req">*</span>}</label>
            <Select
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={[
                { value: 'CONFIGURE_ENABLE', label: 'CONFIGURE_ENABLE' },
                { value: 'ENABLE', label: 'ENABLE' },
              ]}
              value={adCommand ? { value: adCommand, label: adCommand } : null}
              onChange={(opt) => onCommandChange(opt ? opt.value : '')}
              placeholder="Select..."
              isDisabled={!collectorType || collectorType === 'mbb'}
              isClearable
            />
          </div>
        </div>

        {/* MBB Collector: no additional config */}
        {collectorType === 'mbb' && (
          <div className="mbb-blank">
            <div className="mbb-blank-icon">
              <svg viewBox="0 0 24 24" strokeWidth="2">
                <rect x="5" y="2" width="14" height="20" rx="2" />
                <line
                  x1="12"
                  y1="18"
                  x2="12.01"
                  y2="18"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="mbb-blank-title">MBB Collector</div>
            <div className="mbb-blank-sub">
              No additional configuration required for MBB Collector.
              <br />
              It will be automatically provisioned for this session.
            </div>
          </div>
        )}

        {/* AD WWAN Collector */}
        {collectorType === 'adwwan' && (
          <div>
            {/* AD configuration blocks — only for CONFIGURE_ENABLE */}
            {adCommand === 'CONFIGURE_ENABLE' && (
              <div>
                {adEntries.map((entry, idx) => renderADBlock(entry, idx))}
                <button className="add-ad-btn" onClick={addAdEntry}>
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>{' '}
                  Add AD Configuration
                </button>
                {fieldErrors['tel_adContainer'] && (
                  <div className="err-msg show" style={{ marginTop: 8 }}>
                    At least one AD configuration is required.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Config detail modal */}
      {renderConfigModal()}
    </div>
  );
}

export default TelemetryCollectorConfig;
